import { type Component, Show, For, Switch, Match, createSignal } from "solid-js";
import { usePromptBarContext } from "../PromptBarContext";
import { TbAlertTriangle, TbApps, TbAt, TbFileText, TbLoader2, TbX } from "solid-icons/tb";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { ScopeManagerPane } from "./ScopeManagerPane/ScopeManagerPane";

const truncateFilename = (filename: string, maxLength = 25) => {
  if (filename.length <= maxLength) return filename;

  const extension = filename.split(".").pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);

  return `${truncatedName}...${extension}`;
};
const truncateConceptName = (name: string, maxLength = 25) => {
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

export const ScopeDisplay: Component = () => {
  const context = usePromptBarContext();
  const assets = context.scope.assets;
  const concepts = context.scope.concepts;

  const [isOpen, setIsOpen] = createSignal(false);
  const totalItems = () => assets().length + concepts().length;

  return (
    <div class="flex items-center gap-2 flex-wrap text-xs px-3 mb-2 text-secondary-text">
      <StPopover
        noArrow
        open={isOpen()}
        onOpenChange={(open) => setIsOpen(open)}
        gutter={10}
        placement="top-start"
        flip
        contentProps={{ class: "z-20 bg-panel", onOpenAutoFocus: (e) => e.preventDefault() }}
        content={<ScopeManagerPane onClose={() => setIsOpen(false)} />}
      >
        <StPopoverTrigger class="group/btn flex bg-canvas hover:bg-canvas-hover rounded-lg border border-muted-border hover:border-primary-border dark:border-primary-border items-center gap-1 py-1 px-2 cursor-pointer select-none">
          <TbAt class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
          <span>
            {assets().length === 0 ? "Add to scope" : `${totalItems()} item${totalItems() === 1 ? "" : "s"} in scope`}
          </span>
        </StPopoverTrigger>
      </StPopover>

      <For each={concepts()}>
        {(concept) => (
          <span class="group/btn flex bg-concept-bg text-concept-text hover:bg-concept-bg-hover border border-concept-border rounded-lg items-center gap-1 py-1 px-2 select-none">
            <TbApps class="size-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-concept-icon" />
            <span>{truncateConceptName(concept.name)}</span>

            <button
              type="button"
              title={`Remove ${concept.name} from scope`}
              aria-label={`Remove ${concept.name} from scope`}
              onClick={(e) => {
                e.preventDefault();
                context.scope.removeConcept(concept.conceptId);
              }}
            >
              <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
            </button>
          </span>
        )}
      </For>

      <For each={assets()}>
        {(asset) => (
          <span class="group/btn flex bg-asset-bg hover:bg-asset-bg-hover text-asset-text border border-asset-border rounded-lg items-center gap-1 py-1 px-2 select-none">
            <Switch>
              <Match when={asset.status === "uploading"}>
                <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
              </Match>
              <Match when={asset.status === "error"}>
                <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
              </Match>
              <Match when={asset.status === "processing"}>
                <div class="relative">
                  <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
                  <div class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
                </div>
              </Match>
              <Match when={asset.status === "completed"}>
                <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-asset-icon" />
              </Match>
            </Switch>
            <span>{truncateFilename(asset.filename)}</span>

            <button
              type="button"
              title={`Remove ${asset.filename} from scope`}
              aria-label={`Remove ${asset.filename} from scope`}
              onClick={(e) => {
                e.preventDefault();
                context.scope.removeAsset(asset.assetId);
              }}
            >
              <TbX class="size-3 cursor-pointer hover:scale-hover active:scale-active transition-transform" />
            </button>
          </span>
        )}
      </For>

      <Show when={totalItems() > 1}>
        <button
          type="button"
          onClick={context.scope.clearAll}
          class="text-xs text-muted-text hover:text-secondary-text transition-colors underline cursor-pointer select-none"
          title="Clear all assets from scope"
        >
          Clear all
        </button>
      </Show>
    </div>
  );
};
